/**
 * AIRewriteTextField - کامپوننت TextField با قابلیت بازنویسی هوش مصنوعی
 *
 * Props:
 * - value: string - مقدار فعلی متن
 * - onChange: function - تابع برای تغییر مقدار
 * - label: string - لیبل فیلد (پیش‌فرض: 'توضیحات')
 * - apiEndpoint: string - endpoint API برای بازنویسی (پیش‌فرض: '/ai/rewrite')
 * - lang: string - زبان برای بازنویسی (پیش‌فرض: 'fa')
 * - rows: number - تعداد سطرها (پیش‌فرض: 8)
 * - textFieldProps: object - props اضافی برای TextField
 * - ...props: سایر props که به Box بیرونی اعمال می‌شود
 *
 * ویژگی‌ها:
 * - دکمه بازنویسی با هوش مصنوعی
 * - امکان بازگشت به متن اصلی
 * - جابجایی بین نسخه اصلی و بازنویسی شده
 */
import React, { useState } from 'react';
import { TextField, IconButton, CircularProgress, Box } from '@mui/material';
import { AutoFixHigh, Undo, SwapHoriz } from '@mui/icons-material';
import api from '../utils/api';
import { snackbar, successMessage } from '../utils/showMessage';

const AIRewriteTextField = ({
  value,
  onChange,
  label = 'توضیحات',
  apiEndpoint = '/ai/rewrite',
  lang = 'fa',
  rows = 8,
  textFieldProps = {},
  ...props
}) => {
  const [aiLoading, setAiLoading] = useState(false);
  const [originalDescription, setOriginalDescription] = useState();
  const [isUsingRewritten, setIsUsingRewritten] = useState(false);

  const handleAIRewrite = async () => {
    if (!value?.trim()) {
      snackbar('توضیحاتی برای بازنویسی وجود ندارد');
      return;
    }
    try {
      setAiLoading(true);
      const response = await api.post(apiEndpoint, {
        text: value,
        lang: lang
      });
      const newDescription = response.data?.rewrittenText || '';
      if (newDescription) {
        // Save original description if not already saved
        if (!originalDescription) {
          setOriginalDescription(value);
        }
        onChange(newDescription);
        setIsUsingRewritten(true);
        successMessage('توضیحات با هوش مصنوعی بازنویسی شد');
      } else {
        snackbar('پاسخی از هوش مصنوعی دریافت نشد');
      }
    } catch (err) {
      console.error('AI Rewrite Error:', err);
      const msg = err?.response?.data?.message || err.message || 'خطا در بازنویسی با هوش مصنوعی';
      snackbar(msg);
    } finally {
      setAiLoading(false);
    }
  };

  const handleSwitchToOriginal = () => {
    if (originalDescription && isUsingRewritten) {
      onChange(originalDescription);
      setIsUsingRewritten(false);
    }
  };

  const handleToggleVersions = () => {
    if (originalDescription) {
      const currentDesc = value;
      onChange(originalDescription);
      setOriginalDescription(currentDesc);
      setIsUsingRewritten(!isUsingRewritten);
    }
  };

  return (
    <Box sx={{ position: 'relative' }} {...props}>
      <TextField
        fullWidth
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        multiline
        rows={rows}
        {...textFieldProps}
      />
      <Box sx={{ position: 'absolute', top: 8, left: 8, display: 'flex', gap: 0.5 }}>
        <IconButton
          size="small"
          onClick={handleAIRewrite}
          disabled={aiLoading || !value?.trim()}
          sx={{
            bgcolor: 'rgba(255,255,255,0.9)',
            border: '1px solid #ddd',
            '&:hover': { bgcolor: 'white' }
          }}
          title="بازنویسی با هوش مصنوعی"
        >
          {aiLoading ? <CircularProgress size={16} /> : <AutoFixHigh fontSize="small" color="primary" />}
        </IconButton>
        {originalDescription && (
          <>
            <IconButton
              size="small"
              onClick={handleSwitchToOriginal}
              disabled={!isUsingRewritten}
              sx={{
                bgcolor: 'rgba(255,255,255,0.9)',
                border: '1px solid #ddd',
                '&:hover': { bgcolor: 'white' }
              }}
              title="بازگشت به متن اصلی"
            >
              <Undo fontSize="small" color="secondary" />
            </IconButton>
            <IconButton
              size="small"
              onClick={handleToggleVersions}
              sx={{
                bgcolor: 'rgba(255,255,255,0.9)',
                border: '1px solid #ddd',
                '&:hover': { bgcolor: 'white' }
              }}
              title="جابجایی بین نسخه‌ها"
            >
              <SwapHoriz fontSize="small" color="info" />
            </IconButton>
          </>
        )}
      </Box>
    </Box>
  );
};

export default AIRewriteTextField;